import './LocationSection.scss';

import { useEffect, useRef, useState } from 'react';

import MetaAPI from '../../MetaAPI';

const metaAPI = new MetaAPI();
const MIN_CHARS_INTEREST = 2;
const MAX_INTERESTS = 8;

export default function LocationSection({ values, errors, touched, setFieldValue, companyId }) {
    // -- localidades
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);
    const debounceTimer = useRef(null);

    // -- interesses
    const [interestQuery, setInterestQuery] = useState('');
    const [interestOptions, setInterestOptions] = useState([]);
    const [interestLoading, setInterestLoading] = useState(false);
    const [showInterestDropdown, setShowInterestDropdown] = useState(false);
    const interestRef = useRef(null);
    const interestDebounceTimer = useRef(null);

    const [suggestionOptions, setSuggestionOptions] = useState([]);
    const [suggestionLoading, setSuggestionLoading] = useState(false);

    // Info modal
    const [showInfoModal, setShowInfoModal] = useState(false);

    // exemplos (para salão de beleza / manicure)
    const exampleInterests = [
        'Manicure',
        'Pedicure',
        'Salão de Beleza',
        'Cosméticos',
        'Unhas',
        'Maquiagem',
        'Cuidados com a Pele',
        'Esteticista',
        'Produtos para Unhas',
        'Alongamento de Unhas'
    ];

    // Fecha dropdown ao clicar fora (ambos)
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
            if (interestRef.current && !interestRef.current.contains(e.target)) setShowInterestDropdown(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ---------------------------
    // Localizações (cidades)
    // ---------------------------
    const searchLocations = async (text) => {
        if (!text || text.length < 3) {
            setOptions([]);
            setShowDropdown(false);
            return;
        }

        setLoading(true);
        try {
            const res = await metaAPI.getLocations(text, values.adAccountId);
            const data = res?.data || [];
            setOptions(data);
            setShowDropdown(true);
        } catch (err) {
            console.error('Erro ao buscar localizações:', err);
            setOptions([]);
            setShowDropdown(false);
        } finally {
            setLoading(false);
        }
    };

    const handleQueryChange = (text) => {
        setQuery(text);
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            searchLocations(text);
        }, 300);
    };

    const addLocation = (loc) => {
        const list = [...(values.locations || [])];

        if (list.length >= 4) {
            alert('Você pode selecionar até 4 cidades.');
            return;
        }

        const exists = list.some(l => (l.primary_city_id && loc.primary_city_id && l.primary_city_id === loc.primary_city_id)
            || l.name === loc.name);

        if (exists) {
            setQuery('');
            setOptions([]);
            setShowDropdown(false);
            return;
        }

        list.push({
            type: loc.type,
            name: loc.name,
            key: loc.key,
            country_code: loc.country_code,
            region: loc.region,
            primary_city_id: loc.primary_city_id,
            radius: 10,
            unit: 'kilometer'
        });

        setFieldValue('locations', list);
        setQuery('');
        setOptions([]);
        setShowDropdown(false);
    };

    const removeLocation = (index) => {
        const list = [...(values.locations || [])];
        list.splice(index, 1);
        setFieldValue('locations', list);
    };

    // ---------------------------
    // Interesses
    // ---------------------------
    const searchInterests = async (text) => {
        if (!text || text.length < MIN_CHARS_INTEREST) {
            setInterestOptions([]);
            setShowInterestDropdown(false);
            return;
        }

        setInterestLoading(true);
        try {
            const res = await metaAPI.getInterests(text, values.adAccountId || '');
            const data = res?.data || [];
            const items = (data || []).map(it => ({
                id: it.id || it.key || it.name,
                name: it.name,
                path: it.path,
                audience_size: it.audience_size || it.audience_size_lower_bound || null,
                topic: it.topic,
                description: it.description,
                key: it.key
            }));
            setInterestOptions(items);
            setShowInterestDropdown(true);
        } catch (err) {
            console.error('Erro ao buscar interesses:', err);
            setInterestOptions([]);
            setShowInterestDropdown(false);
        } finally {
            setInterestLoading(false);
        }
    };

    const handleInterestQueryChange = (text) => {
        setInterestQuery(text);
        if (interestDebounceTimer.current) clearTimeout(interestDebounceTimer.current);
        interestDebounceTimer.current = setTimeout(() => {
            searchInterests(text);
        }, 300);
    };

    const addInterest = (interest) => {
        const list = [...(values.interests || [])];

        if (list.length >= MAX_INTERESTS) {
            alert(`Você pode adicionar até ${MAX_INTERESTS} interesses.`);
            return;
        }

        const id = interest.id || interest.key || interest.name;
        const exists = list.some(i => (i.id && id && i.id === id) || i.name === interest.name);
        if (exists) {
            setInterestQuery('');
            setInterestOptions([]);
            setShowInterestDropdown(false);
            return;
        }

        list.push({
            id,
            name: interest.name,
            audience_size: interest.audience_size,
            path: interest.path,
            key: interest.key
        });

        setFieldValue('interests', list);
        setInterestQuery('');
        setInterestOptions([]);
        setSuggestionOptions(prev => prev.filter(p => p.id !== id));
        setShowInterestDropdown(false);
    };

    const removeInterest = (index) => {
        const list = [...(values.interests || [])];
        list.splice(index, 1);
        setFieldValue('interests', list);
    };

    // handler do botão "Ver sugestões"
    async function fetchInterestSuggestions() {
        const interestList = (values.interests || []).map(i => i.name).filter(Boolean);
        if (interestList.length === 0 && interestQuery.length < MIN_CHARS_INTEREST) return;

        setSuggestionLoading(true);
        try {
            const listToSend = interestList.length ? interestList : [interestQuery];
            const res = await metaAPI.getSeggestedInterests(listToSend);
            const json = res.data;
            const items = (json.data || []).map(it => ({
                id: it.id || it.key || it.name,
                name: it.name,
                path: it.path,
                audience_size: it.audience_size || it.audience_size_lower_bound || null,
                topic: it.topic,
                description: it.description,
                key: it.key
            }));

            const selectedIds = new Set((values.interests || []).map(i => i.id));
            const filtered = items.filter(i => !selectedIds.has(i.id)).slice(0, 12);
            setSuggestionOptions(filtered);
            setShowInterestDropdown(true);
        } catch (err) {
            console.error(err);
        } finally {
            setSuggestionLoading(false);
        }
    }

    const addSuggestion = (opt) => {
        addInterest(opt);
        setSuggestionOptions(prev => prev.filter(p => p.id !== opt.id));
    };

    // adicionar exemplo do modal (usa addInterest)
    const addExampleInterest = (name) => {
        addInterest({ name });
        // fechar modal opcional: manter aberto para permitir múltiplos adds
    };

    // ---------------------------
    // Render
    // ---------------------------
    return (
        <div className="location-section">
            <div className="section-header">
                <h3>📍 Localização e Público de Interesse</h3>
                <p>Defina onde seu anúncio será exibido e quais interesses do público você deseja segmentar</p>
            </div>

            {/* Localizações */}
            <div className="loc-row">
                <label>
                    Adicionar cidades
                    <span className="info-icon" title="Digite o nome da cidade para buscar">ℹ️</span>
                </label>

                <div className="autocomplete-wrapper" ref={searchRef}>
                    <input
                        type="text"
                        className="location-input"
                        placeholder="Digite o nome da cidade (ex: São Paulo)"
                        value={query}
                        onChange={(e) => handleQueryChange(e.target.value)}
                        onFocus={() => query.length >= 3 && setShowDropdown(true)}
                    />

                    {loading && <div className="small-loading">🔄 Buscando...</div>}

                    {showDropdown && options.length > 0 && (
                        <div className="dropdown">
                            {options.map((opt, i) => (
                                <div
                                    key={opt.key ?? opt.primary_city_id ?? i}
                                    className="dropdown-item"
                                    onClick={() => addLocation(opt)}
                                >
                                    <div className="location-name">{opt.name}</div>
                                    <div className="location-details">
                                        {opt.region ? `${opt.region}, ` : ''}{opt.country_code}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {showDropdown && !loading && options.length === 0 && query.length >= 3 && (
                        <div className="dropdown">
                            <div className="dropdown-item empty">Nenhuma cidade encontrada</div>
                        </div>
                    )}
                </div>

                {touched.locations && errors.locations && (
                    <div className="error">{errors.locations}</div>
                )}
            </div>

            {/* Lista de Localizações Adicionadas */}
            {values.locations && values.locations.length > 0 && (
                <div className="loc-list">
                    <div className="list-title">Cidades selecionadas:</div>
                    {values.locations.map((l, i) => (
                        <div key={l.primary_city_id ?? l.name ?? i} className="loc-chip">
                            <span className="chip-icon">📍</span>
                            <span className="chip-text">
                                {l.name}{l.region && `, ${l.region}`} • {l.country_code}
                            </span>
                            <button type="button" className="chip-remove" onClick={() => removeLocation(i)}>✕</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Público Advantage */}
            {/* <div className="switch-row">
                <div className="switch-info">
                    <label>Público Advantage de Localização</label>
                    <p className="switch-description">
                        Permite que o Facebook expanda automaticamente para localizações próximas para melhorar o desempenho
                    </p>
                </div>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={values.locationAdvantage}
                        onChange={(e) => setFieldValue('locationAdvantage', e.target.checked)}
                    />
                    <span className="slider" />
                </label>
            </div> */}

            <div style={{ marginTop: 20 }} />

            {/* Interesses (1 input + botão sugestões) */}
            <div className="loc-row">
                <label>
                    Perfil de Interesses
                    <span
                        className="info-icon"
                        title="Dicas para escolher interesses"
                        role="button"
                        onClick={() => setShowInfoModal(true)}
                        style={{ cursor: 'pointer' }}
                    >ℹ️</span>
                </label>

                <div style={{ marginBottom: 8, color: '#6b7280', fontSize: 13 }}>
                    Interesses são temas/afinidades (ex: "manicure", "cosméticos"). Você pode adicionar até {MAX_INTERESTS} interesses.
                </div>

                <div className="autocomplete-wrapper" ref={interestRef}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                            type="text"
                            className="location-input"
                            placeholder="Pesquisar interesses (ex: beleza, maquiagem)"
                            value={interestQuery}
                            onChange={(e) => handleInterestQueryChange(e.target.value)}
                            onFocus={() => interestQuery.length >= MIN_CHARS_INTEREST && setShowInterestDropdown(true)}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {interestLoading && <div className="small-loading">🔄 Buscando...</div>}

                    {showInterestDropdown && (
                        <div className="dropdown">
                            {/* Resultados do autocomplete (se houver) */}
                            {interestOptions.length > 0 && (
                                <>
                                    <div className="dropdown-section-title">Resultados</div>
                                    {interestOptions.map((opt, i) => (
                                        <div key={opt.id ?? i} className="dropdown-item" onClick={() => addInterest(opt)}>
                                            <div className="location-name">{opt.name}</div>
                                            <div className="location-details">
                                                {opt.path ? (Array.isArray(opt.path) ? opt.path.join(' › ') : opt.path) : null}
                                                {opt.audience_size ? ` • ${Number(opt.audience_size).toLocaleString()} pessoas` : null}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* Sugestões separadas */}
                            {suggestionOptions.length > 0 && (
                                <>
                                    <div className="dropdown-section-title">Sugestões</div>
                                    {suggestionOptions.map((opt, i) => (
                                        <div key={opt.id ?? `s${i}`} className="dropdown-item suggestion" onClick={() => addSuggestion(opt)}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div className="location-name">
                                                        {opt.name} <span className="suggest-badge">Sugerido</span>
                                                    </div>
                                                    <div className="location-details">
                                                        {opt.path ? (Array.isArray(opt.path) ? opt.path.join(' › ') : opt.path) : null}
                                                        {opt.audience_size ? ` • ${Number(opt.audience_size).toLocaleString()} pessoas` : null}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="add-suggestion-btn"
                                                    onClick={(e) => { e.stopPropagation(); addSuggestion(opt); }}
                                                    aria-label={`Adicionar ${opt.name}`}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            {/* vazio */}
                            {!interestLoading && interestOptions.length === 0 && suggestionOptions.length === 0 && interestQuery.length >= MIN_CHARS_INTEREST && (
                                <div className="dropdown">
                                    <div className="dropdown-item empty">Nenhum interesse encontrado</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {touched.interests && errors.interests && (
                    <div className="error">{errors.interests}</div>
                )}
            </div>

            {/* Lista de interesses adicionados */}
            {values.interests && values.interests.length > 0 && (
                <div className="loc-list">
                    <div className="list-title">Interesses selecionados:</div>
                    {values.interests.map((itm, i) => (
                        <div key={itm.id ?? itm.name ?? i} className="loc-chip">
                            <span className="chip-icon">🎯</span>
                            <span className="chip-text">
                                {itm.name}{itm.audience_size ? ` • ${Number(itm.audience_size).toLocaleString()}` : ''}
                            </span>
                            <button type="button" className="chip-remove" onClick={() => removeInterest(i)}>✕</button>
                        </div>
                    ))}
                </div>
            )}

            {/* Info Modal */}
            {showInfoModal && (
                <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
                    <div className="info-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="info-modal-header">
                            <h4>Dicas — Como escolher interesses (ex.: Salão de Beleza / Manicure)</h4>
                            <button className="modal-close" onClick={() => setShowInfoModal(false)}>✕</button>
                        </div>
                        <div className="info-modal-body">
                            <p>
                                Para um salão de beleza ou manicure, comece adicionando termos práticos que descrevem seu serviço e público.
                                Exemplos abaixo — clique para adicionar rapidamente.
                            </p>

                            <div className="examples-grid">
                                {exampleInterests.map((ex) => (
                                    <button
                                        key={ex}
                                        type="button"
                                        className="example-chip"
                                        onClick={() => addExampleInterest(ex)}
                                    >
                                        {ex}
                                    </button>
                                ))}
                            </div>

                            <div className="modal-note">
                                <strong>Dica rápida:</strong> combine termos gerais (ex: "Salão de Beleza") com termos específicos (ex: "Manicure", "Alongamento de Unhas") para alcançar públicos mais relevantes.
                            </div>

                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => setShowInfoModal(false)}>Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}